import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, LogOut, Check, X, Clock, User, Building2, Loader2, ShieldCheck } from "lucide-react";

const TABS = [
  { key: "pending", label: "Čakajúce" },
  { key: "approved", label: "Schválené" },
  { key: "rejected", label: "Zamietnuté" },
];

export default function Admin() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ users: [], stats: { pending: 0, approved: 0, rejected: 0 } });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { status: t } });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const act = async (id, action) => {
    setActing(id + action);
    try {
      await api.post(`/admin/${action}/${id}`);
      toast.success(action === "approve" ? "Účet schválený, email odoslaný" : "Účet zamietnutý, email odoslaný");
      load(tab);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setActing(null); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-6 py-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-700 flex items-center justify-center"><Sparkles className="text-white" size={16} /></div>
          <span className="font-head font-bold tracking-tight">Profeeluj<span className="text-blue-700"> AI</span></span>
          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700"><ShieldCheck size={13} /> Admin</span>
          <button data-testid="admin-logout-btn" onClick={() => { logout(); nav("/"); }} className="ml-auto text-slate-500 hover:text-slate-800 transition-colors"><LogOut size={18} /></button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8">
        <h1 className="font-head text-3xl font-bold tracking-tight">Schvaľovanie registrácií</h1>
        <p className="mt-2 text-slate-600">Schváľte alebo zamietnite nové účty. Používateľ dostane email o rozhodnutí.</p>

        <div className="mt-6 flex gap-2">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === t.key ? "bg-blue-700 text-white" : "bg-white text-slate-600 border border-slate-300 hover:bg-slate-50"}`}>
              {t.label}
              <span className={`rounded-full px-2 py-0.5 text-xs ${tab === t.key ? "bg-white/25" : "bg-slate-100"}`}>{data.stats?.[t.key] ?? 0}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-700" /></div>
        ) : data.users.length === 0 ? (
          <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100"><Clock className="text-slate-400" /></div>
            <p className="mt-4 font-semibold text-slate-800">Žiadne {tab === "pending" ? "čakajúce" : tab === "approved" ? "schválené" : "zamietnuté"} účty</p>
          </div>
        ) : (
          <div className="mt-6 space-y-3" data-testid="admin-user-list">
            {data.users.map((u) => (
              <div key={u.id} data-testid={`admin-user-${u.id}`} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 flex items-center gap-4">
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${u.role === "company" ? "bg-emerald-50" : "bg-blue-50"}`}>
                  {u.role === "company" ? <Building2 className="text-emerald-600" size={20} /> : <User className="text-blue-700" size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-head font-semibold text-slate-900 truncate">{u.name}{u.company_name ? ` · ${u.company_name}` : ""}</div>
                  <div className="text-sm text-slate-500 truncate">{u.email}</div>
                  <span className="mt-1 inline-block text-xs font-medium text-slate-400">{u.role === "company" ? "Firma" : "Kandidát"}</span>
                </div>
                {tab === "pending" ? (
                  <div className="flex gap-2">
                    <button data-testid={`reject-${u.id}`} onClick={() => act(u.id, "reject")} disabled={acting}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
                      {acting === u.id + "reject" ? <Loader2 className="animate-spin" size={15} /> : <X size={15} />} Zamietnuť
                    </button>
                    <button data-testid={`approve-${u.id}`} onClick={() => act(u.id, "approve")} disabled={acting}
                      className="inline-flex items-center gap-1.5 rounded-full bg-blue-700 text-white px-4 py-2.5 text-sm font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50">
                      {acting === u.id + "approve" ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Schváliť
                    </button>
                  </div>
                ) : (
                  <StatusBadge status={u.status} />
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: { txt: "Schválený", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Check },
    rejected: { txt: "Zamietnutý", cls: "bg-red-50 text-red-600 border-red-200", Icon: X },
    pending: { txt: "Čaká", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  };
  const s = map[status] || map.pending;
  return <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold ${s.cls}`}><s.Icon size={14} /> {s.txt}</span>;
}
